import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePageSearch, matchesSearch, noMatchText } from '../lib/pageSearch';
import { getProjects, getUsers, createIntakeProject, assignPM } from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import { Avatar } from '../components/ui';

/* ═══════════════════════════════════════════════════════════════════════════
   Pre-Construction page — warm-cream + sage redesign
   Top-level route (/pre-construction, role-gated): pipeline of sold-but-not-
   yet-active projects in constructibility review. Leadership assigns a PM,
   which activates the project. Token-only colors. Visual reskin — API +
   role-gating unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

const $f = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(+v || 0);

const ROLE_LABEL = {
  admin: 'Admin',
  vp: 'Vice President',
  construction_director: 'Const. Director',
  construction_manager: 'Const. Manager',
  pm: 'Project Manager',
};

const fullName = (u) => `${u.first_name || ''} ${u.last_name || ''}`.trim();

const initialsOf = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const ring = (c) => `color-mix(in oklch, ${c} 22%, transparent)`;
const ageTone = (n) => (n == null ? 'var(--text-3)' : n > 30 ? 'var(--danger)' : n > 14 ? 'var(--warning)' : 'var(--text-1)');

/* ─────────────── Inline SVG icons ─────────────── */

const Icon = ({ name, size = 16, sw = 1.6 }) => {
  const s = size;
  const p = (d) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      {d}
    </svg>
  );
  switch (name) {
    case 'refresh':
      return p(
        <>
          <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
          <path d="M3 21v-5h5" />
        </>
      );
    case 'plus':
      return p(<path d="M12 5v14M5 12h14" />);
    case 'x':
      return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'check':
      return p(<path d="m5 12 5 5L20 7" />);
    case 'clock':
      return p(
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 2" />
        </>
      );
    case 'user':
      return p(
        <>
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
        </>
      );
    case 'user-plus':
      return p(
        <>
          <circle cx="9" cy="8" r="3.3" />
          <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
          <path d="M19 8v6M22 11h-6" />
        </>
      );
    case 'chevron-d':
      return p(<path d="m6 9 6 6 6-6" />);
    case 'arrow-up-r':
      return p(
        <>
          <path d="M7 17 17 7" />
          <path d="M8 7h9v9" />
        </>
      );
    case 'spin':
      return (
        <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" style={{ display: 'block', animation: 'pSpin .8s linear infinite' }}>
          <path d="M21 12a9 9 0 1 1-6.2-8.55" />
        </svg>
      );
    default:
      return null;
  }
};

/* ─────────────── Buttons ─────────────── */

const bGhost = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 34,
  padding: '0 12px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-2)',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  boxShadow: 'var(--shadow-xs)',
  cursor: 'pointer',
};
const bPrimary = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 34,
  padding: '0 14px 0 12px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  color: '#fff',
  background: 'var(--accent)',
  border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))',
  boxShadow: '0 1px 0 oklch(0.42 0.06 135), 0 1px 2px oklch(0.4 0.05 135 / 0.25)',
  cursor: 'pointer',
};

/* ─────────────── Action row ─────────────── */

const ActionRow = ({ onRefresh, onNewIntake }) => (
  <header style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 2px' }}>
    <p style={{ flex: 1, minWidth: 0, margin: 0, fontSize: 15, color: 'var(--text-3)', letterSpacing: '-0.005em', lineHeight: 1.45 }}>
      Projects in constructibility review — assign to a PM when sold.
    </p>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
      <button type="button" className="p-refresh" onClick={onRefresh} style={bGhost}>
        <Icon name="refresh" size={13} /> Refresh
      </button>
      <button type="button" className="p-newbtn" onClick={onNewIntake} style={bPrimary}>
        <Icon name="plus" size={15} sw={2.2} /> New Intake
      </button>
    </div>
  </header>
);

/* ─────────────── KPI row ─────────────── */

const KpiTile = ({ label, value, valueColor, muted, fill, ringColor }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 7,
      flex: '1 1 200px',
      minWidth: 180,
      padding: '16px 20px',
      borderRadius: 10,
      background: fill || 'transparent',
      border: ringColor ? `1px solid ${ringColor}` : '1px solid transparent',
    }}
  >
    <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
    <div
      className="mono"
      style={{
        fontSize: 29.5,
        fontWeight: 800,
        lineHeight: 1,
        color: muted ? 'var(--text-4)' : valueColor || 'var(--text-1)',
        letterSpacing: '-0.03em',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value}
    </div>
  </div>
);

const KpiDivider = () => <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-subtle)', margin: '10px 0', flex: '0 0 auto' }} />;

const KpiRow = ({ stats }) => (
  <section
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'stretch',
      padding: 6,
      gap: 0,
      background: 'var(--bg-surface)',
      border: '1px solid var(--border-default)',
      borderRadius: 12,
      boxShadow: 'var(--shadow-sm)',
    }}
  >
    <KpiTile
      label="In Pipeline"
      value={stats.pipeline}
      valueColor="var(--accent-text)"
      muted={stats.pipeline === 0}
      fill={stats.pipeline === 0 ? null : 'var(--accent-soft)'}
      ringColor={stats.pipeline === 0 ? null : ring('var(--accent)')}
    />
    <KpiDivider />
    <KpiTile
      label="Estimated Value"
      value={$f(stats.value)}
      valueColor="var(--success)"
      muted={stats.value === 0}
      fill={stats.value === 0 ? null : 'var(--success-soft)'}
      ringColor={stats.value === 0 ? null : ring('var(--success)')}
    />
    <KpiDivider />
    <KpiTile
      label="Unassigned"
      value={stats.unassigned}
      valueColor="var(--warning)"
      muted={stats.unassigned === 0}
      fill={stats.unassigned === 0 ? null : 'var(--warning-soft)'}
      ringColor={stats.unassigned === 0 ? null : ring('var(--warning)')}
    />
  </section>
);

/* ─────────────── Project row-card ─────────────── */

const ColLabel = ({ children }) => (
  <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 7 }}>{children}</div>
);

const ProjectRow = ({ project, users, onOpenProject, onAssign, canAssign }) => {
  const assignedTo = (project.construction_manager || '').trim();
  const pmName = assignedTo ? assignedTo.split(',')[0].trim() : null;
  const assignedUser = pmName ? users.find((u) => fullName(u) === pmName) : null;
  const stripeTone = assignedTo ? 'var(--accent)' : 'var(--warning)';
  const age = daysSince(project.created_at);
  const noValue = !(parseFloat(project.contract_amount) > 0);

  return (
    <article
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-default)',
        borderRadius: 12,
        boxShadow: `inset 3px 0 0 ${stripeTone}, var(--shadow-sm)`,
        padding: '16px 20px 16px 22px',
        display: 'grid',
        gap: 16,
        alignItems: 'center',
        gridTemplateColumns: 'minmax(260px, 1fr) 132px 172px 96px auto',
      }}
    >
      {/* Identity */}
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
          <span
            style={{
              fontSize: 16.5,
              fontWeight: 700,
              color: 'var(--text-1)',
              letterSpacing: '-0.012em',
              lineHeight: 1.25,
              flex: '0 1 auto',
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {project.project_name}
          </span>
          {project.project_number && (
            <span
              className="mono"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                flex: '0 0 auto',
                height: 19,
                padding: '0 8px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                color: 'var(--accent-text)',
                background: 'var(--accent-soft)',
                border: `1px solid ${ring('var(--accent)')}`,
              }}
            >
              {project.project_number}
            </span>
          )}
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span style={{ fontWeight: 500, color: 'var(--text-2)' }}>{project.client_name || 'No client'}</span>
          {project.description && (
            <>
              <span style={{ color: 'var(--text-4)', margin: '0 7px' }}>·</span>
              <span>{project.description}</span>
            </>
          )}
        </div>
      </div>

      {/* Est. Value */}
      <div style={{ minWidth: 0 }}>
        <ColLabel>Est. Value</ColLabel>
        <div
          className="mono"
          style={{ fontSize: 15.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', color: noValue ? 'var(--text-4)' : 'var(--text-1)' }}
        >
          {noValue ? '—' : $f(project.contract_amount)}
        </div>
      </div>

      {/* Assigned To */}
      <div style={{ minWidth: 0 }}>
        <ColLabel>Assigned To</ColLabel>
        {assignedTo ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
            <Avatar initials={initialsOf(pmName)} size={28} accent />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pmName}</span>
              {assignedUser && (
                <span style={{ fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {ROLE_LABEL[assignedUser.role] || assignedUser.role}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--warning)', fontWeight: 600, fontSize: 14.5 }}>
            <Icon name="user" size={15} sw={1.8} /> Unassigned
          </div>
        )}
      </div>

      {/* In Pipeline (age) */}
      <div style={{ minWidth: 0 }}>
        <ColLabel>In Pipeline</ColLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: ageTone(age) }}>
          <Icon name="clock" size={14} sw={1.8} />
          <span className="mono" style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>
            {age == null ? '—' : age}
          </span>
          {age != null && <span style={{ fontSize: 13.5, color: 'var(--text-3)', fontWeight: 500 }}>{age === 1 ? 'day' : 'days'}</span>}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto', justifySelf: 'end' }}>
        <button
          type="button"
          className="p-open"
          onClick={() => onOpenProject && onOpenProject(project.slug || project.id)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            height: 32,
            padding: '0 12px',
            borderRadius: 7,
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-2)',
            whiteSpace: 'nowrap',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-default)',
            boxShadow: 'var(--shadow-xs)',
            cursor: 'pointer',
            transition: 'background 120ms, border-color 120ms, color 120ms',
          }}
        >
          <Icon name="arrow-up-r" size={13} sw={1.8} /> Open
        </button>
        {canAssign && (
          <button type="button" className="p-assign" onClick={() => onAssign(project)} style={{ ...bPrimary, height: 32, padding: '0 13px 0 11px', borderRadius: 7 }}>
            <Icon name="user-plus" size={14} sw={1.9} /> Assign PM
          </button>
        )}
      </div>
    </article>
  );
};

/* ─────────────── Modal shell + fields ─────────────── */

const ModalShell = ({ title, subtitle, onClose, busy, children, footer, width = 520 }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'grid',
        placeItems: 'center',
        background: 'oklch(0.2 0.008 100 / 0.34)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        padding: 24,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width,
          maxWidth: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 14,
          boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)',
          overflow: 'hidden',
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-1)',
          letterSpacing: '-0.005em',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90%',
          minHeight: 0,
        }}
      >
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>{title}</h2>
            {subtitle && <p style={{ margin: '5px 0 0', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5 }}>{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="Close"
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              color: 'var(--text-3)',
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: '1px solid transparent',
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            <Icon name="x" size={14} />
          </button>
        </header>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px' }}>{children}</div>
        {footer && (
          <footer style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
};

const FieldLabel = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '0.01em', marginBottom: 6 }}>
    {children}
    {required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
  </label>
);

const focusRing = (f) => ({
  border: `1px solid ${f ? 'var(--accent)' : 'var(--border-default)'}`,
  boxShadow: f ? '0 0 0 3px var(--accent-ring)' : 'none',
});

const TextInput = ({ value, onChange, placeholder, mono, autoFocus }) => {
  const [f, setF] = useState(false);
  return (
    <input
      value={value}
      autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setF(true)}
      onBlur={() => setF(false)}
      placeholder={placeholder}
      style={{
        width: '100%',
        height: 36,
        padding: '0 12px',
        borderRadius: 8,
        boxSizing: 'border-box',
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
        fontSize: 14.5,
        color: 'var(--text-1)',
        background: 'var(--bg-surface)',
        outline: 'none',
        letterSpacing: mono ? '-0.02em' : '-0.005em',
        fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
        transition: 'border-color 120ms, box-shadow 120ms',
        ...focusRing(f),
      }}
    />
  );
};

const MoneyInput = ({ value, onChange, placeholder = '0.00' }) => {
  const [f, setF] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 36,
        padding: '0 12px',
        borderRadius: 8,
        background: 'var(--bg-surface)',
        transition: 'border-color 120ms, box-shadow 120ms',
        ...focusRing(f),
      }}
    >
      <span style={{ fontSize: 14.5, color: 'var(--text-4)', flex: '0 0 auto' }}>$</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        placeholder={placeholder}
        inputMode="decimal"
        type="number"
        className="mono"
        style={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          border: 0,
          outline: 'none',
          background: 'transparent',
          font: 'inherit',
          fontFamily: 'var(--font-mono)',
          fontSize: 14.5,
          color: 'var(--text-1)',
          letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums',
        }}
      />
    </div>
  );
};

const Select = ({ value, onChange, options, placeholder = 'Select…' }) => {
  const [f, setF] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setF(true)}
        onBlur={() => setF(false)}
        style={{
          width: '100%',
          height: 36,
          padding: '0 34px 0 12px',
          borderRadius: 8,
          boxSizing: 'border-box',
          fontFamily: 'var(--font-sans)',
          fontSize: 14.5,
          color: value ? 'var(--text-1)' : 'var(--text-4)',
          background: 'var(--bg-surface)',
          outline: 'none',
          appearance: 'none',
          WebkitAppearance: 'none',
          cursor: 'pointer',
          transition: 'border-color 120ms, box-shadow 120ms',
          ...focusRing(f),
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span style={{ position: 'absolute', top: 0, right: 11, height: 36, display: 'inline-flex', alignItems: 'center', color: 'var(--text-3)', pointerEvents: 'none' }}>
        <Icon name="chevron-d" size={15} sw={1.8} />
      </span>
    </div>
  );
};

const TextArea = ({ value, onChange, placeholder, rows = 3 }) => {
  const [f, setF] = useState(false);
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setF(true)}
      onBlur={() => setF(false)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        resize: 'vertical',
        minHeight: rows * 22 + 4,
        padding: '10px 12px',
        borderRadius: 8,
        fontFamily: 'var(--font-sans)',
        fontSize: 14.5,
        lineHeight: 1.5,
        color: 'var(--text-1)',
        background: 'var(--bg-surface)',
        outline: 'none',
        transition: 'border-color 120ms, box-shadow 120ms',
        ...focusRing(f),
      }}
    />
  );
};

const SubmitBtn = ({ children, icon, tone = 'accent', busy, disabled, onClick }) => {
  const bg = tone === 'success' ? 'var(--success)' : 'var(--accent)';
  const off = disabled && !busy;
  return (
    <button
      type="button"
      onClick={() => !busy && !disabled && onClick()}
      disabled={busy || disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 34,
        padding: '0 14px',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        color: '#fff',
        background: bg,
        border: `1px solid color-mix(in oklch, ${bg} 80%, oklch(0.2 0.02 135))`,
        boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.18)',
        whiteSpace: 'nowrap',
        cursor: busy || disabled ? 'default' : 'pointer',
        opacity: off ? 0.5 : busy ? 0.85 : 1,
      }}
    >
      {busy ? <Icon name="spin" size={13} sw={2.4} /> : icon && <Icon name={icon} size={13} sw={2.2} />}
      {children}
    </button>
  );
};

/* ─────────────── Intake + Assign modals ─────────────── */

const IntakeModal = ({ users, busy, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    project_name: '',
    project_number: '',
    client_name: '',
    estimated_value: '',
    assigned_to: '',
    description: '',
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const canCreate = form.project_name.trim().length > 0;

  const ownerOptions = users
    .filter((u) => ['construction_director', 'construction_manager', 'admin', 'vp'].includes(u.role) && u.is_active)
    .map((u) => ({ value: fullName(u), label: `${fullName(u)} · ${ROLE_LABEL[u.role] || u.role}` }));

  return (
    <ModalShell
      title="New Project Intake"
      width={520}
      busy={busy}
      onClose={onClose}
      subtitle="Create a pre-construction project — the same data the sales app sends."
      footer={
        <>
          <button type="button" style={{ ...bGhost, opacity: busy ? 0.6 : 1 }} onClick={() => !busy && onClose()}>
            Cancel
          </button>
          <SubmitBtn icon="plus" tone="accent" busy={busy} disabled={!canCreate} onClick={() => onSubmit(form)}>
            {busy ? 'Creating…' : 'Create Project'}
          </SubmitBtn>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <FieldLabel required>Project Name</FieldLabel>
          <TextInput value={form.project_name} onChange={set('project_name')} placeholder="e.g. Riverside Medical Center" autoFocus />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <FieldLabel>Project Number</FieldLabel>
            <TextInput value={form.project_number} onChange={set('project_number')} placeholder="e.g. 2026-045" mono />
          </div>
          <div>
            <FieldLabel>Client Name</FieldLabel>
            <TextInput value={form.client_name} onChange={set('client_name')} placeholder="Client or owner" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <FieldLabel>Estimated Contract Value</FieldLabel>
            <MoneyInput value={form.estimated_value} onChange={set('estimated_value')} />
          </div>
          <div>
            <FieldLabel>Assign To (CD / CM)</FieldLabel>
            <Select value={form.assigned_to} onChange={set('assigned_to')} placeholder="— Unassigned —" options={ownerOptions} />
          </div>
        </div>
        <div>
          <FieldLabel>Description / Notes</FieldLabel>
          <TextArea value={form.description} onChange={set('description')} rows={3} placeholder="Scope summary, constructibility concerns, or notes from the sales handoff…" />
        </div>
      </div>
    </ModalShell>
  );
};

const AssignModal = ({ project, users, busy, onClose, onSubmit }) => {
  const [pm, setPm] = useState('');
  const canAssign = pm.trim().length > 0;
  const pmOptions = users.filter((u) => u.is_active).map((u) => ({ value: fullName(u), label: `${fullName(u)} · ${ROLE_LABEL[u.role] || u.role}` }));

  return (
    <ModalShell
      title="Assign PM & Activate"
      width={440}
      busy={busy}
      onClose={onClose}
      subtitle={
        <>
          <strong style={{ color: 'var(--text-2)', fontWeight: 600 }}>{project.project_name}</strong> will move to Active and be assigned to the selected PM.
        </>
      }
      footer={
        <>
          <button type="button" style={{ ...bGhost, opacity: busy ? 0.6 : 1 }} onClick={() => !busy && onClose()}>
            Cancel
          </button>
          <SubmitBtn icon="check" tone="success" busy={busy} disabled={!canAssign} onClick={() => onSubmit(pm)}>
            {busy ? 'Activating…' : 'Assign & Activate'}
          </SubmitBtn>
        </>
      }
    >
      <div>
        <FieldLabel required>Select Project Manager</FieldLabel>
        <Select value={pm} onChange={setPm} placeholder="Choose a project manager…" options={pmOptions} />
        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--text-3)', lineHeight: 1.5 }}>
          Assigning a PM activates the project and removes it from the pre-construction pipeline.
        </p>
      </div>
    </ModalShell>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */

const PreConstructionPage = ({ onOpenProject, currentUser }) => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIntake, setShowIntake] = useState(false);
  const [assignProject, setAssignProject] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const isLeadership = currentUser && ['admin', 'vp'].includes(currentUser.role);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [projectData, userData] = await Promise.all([getProjects({ status: 'Pre-Construction' }), getUsers().catch(() => [])]);

      let filtered = Array.isArray(projectData) ? projectData : [];
      if (!isLeadership && currentUser) {
        const myName = `${currentUser.first_name} ${currentUser.last_name}`;
        filtered = filtered.filter((p) => (p.construction_manager || '').split(',').map((s) => s.trim()).includes(myName));
      }
      setProjects(filtered);
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (err) {
      console.error('Failed to load pre-construction data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isLeadership]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stats = useMemo(
    () => ({
      pipeline: projects.length,
      value: projects.reduce((s, p) => s + (parseFloat(p.contract_amount) || 0), 0),
      unassigned: projects.filter((p) => !(p.construction_manager || '').trim()).length,
    }),
    [projects]
  );

  // The top bar's search (lib/pageSearch): name, number, client, description, who it's assigned to.
  const search = usePageSearch('Search pre-construction projects…');
  const shownProjects = useMemo(
    () => projects.filter((p) => matchesSearch(search, p.project_name, p.project_number, p.client_name,
      p.description, (p.construction_manager || '').trim() || 'Unassigned')),
    [projects, search]
  );

  const handleIntakeSubmit = async (form) => {
    if (!form.project_name.trim() || submitting) return;
    try {
      setSubmitting(true);
      await createIntakeProject({ ...form, source: 'manual' });
      setShowIntake(false);
      await loadData();
    } catch (err) {
      console.error('Intake failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignSubmit = async (pm) => {
    if (!assignProject || !pm || assigning) return;
    try {
      setAssigning(true);
      await assignPM(assignProject.id, { project_manager: pm });
      setAssignProject(null);
      await loadData();
    } catch (err) {
      console.error('Failed to assign PM:', err);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) return <LoadingState message="Loading pre-construction projects…" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes pSpin { to { transform: rotate(360deg); } }
        .p-refresh:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .p-newbtn:hover, .p-assign:hover { background: var(--accent-hover); }
        .p-open:hover { border-color: color-mix(in oklch, var(--accent) 30%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
      `}</style>

      <ActionRow onRefresh={loadData} onNewIntake={() => setShowIntake(true)} />
      <KpiRow stats={stats} />

      {shownProjects.length === 0 && projects.length > 0 ? (
        <StateScreen kind="empty" {...noMatchText(search)} />
      ) : shownProjects.length === 0 ? (
        <StateScreen kind="empty" title="No pre-construction projects" body="Create a new intake, or wait for the sales app to send one." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {shownProjects.map((project) => (
            <ProjectRow
              key={project.id}
              project={project}
              users={users}
              onOpenProject={onOpenProject}
              onAssign={(p) => setAssignProject(p)}
              canAssign={isLeadership}
            />
          ))}
        </div>
      )}

      {showIntake && <IntakeModal users={users} busy={submitting} onClose={() => setShowIntake(false)} onSubmit={handleIntakeSubmit} />}
      {assignProject && (
        <AssignModal project={assignProject} users={users} busy={assigning} onClose={() => setAssignProject(null)} onSubmit={handleAssignSubmit} />
      )}
    </div>
  );
};

export default PreConstructionPage;
